import { create } from "zustand";
import type { AgentEvent, AgentStatus, HumanTaskProposal } from "@/lib/agent/types";
import type { QuestInstructionsDraft } from "@/lib/questInstructions";

/** A completed turn in the conversation */
export interface ConversationTurn {
  role: "user" | "assistant";
  text: string;
  toolEvents: AgentEvent[];
}

interface AgentStreamState {
  // Current run
  activeRunId: string | null;
  status: AgentStatus;

  // Conversation history (completed turns)
  conversationHistory: ConversationTurn[];

  // Current turn (in-progress streaming)
  events: AgentEvent[];
  streamedText: string;
  tokensUsed: number;
  iteration: number;

  // Pause state
  clarificationQuestion: string | null;
  clarificationOptions: string[] | null;
  humanTasks: HumanTaskProposal[] | null;
  humanTasksTemplate: { maxSlots: number } | null;
  humanTasksBrief: QuestInstructionsDraft | null;
  fundWalletRequest: { reason: string; suggestedAmountUsd?: number } | null;

  // UI state
  selectedHistoryRunId: string | null;
  settingsOpen: boolean;

  // Actions
  startRun: (runId: string) => void;
  continueTurn: (userMessage: string) => void;
  pushEvent: (event: AgentEvent) => void;
  reset: () => void;
  newChat: () => void;
  loadSession: (runId: string, turns: ConversationTurn[], status: AgentStatus) => void;
  setSelectedHistoryRunId: (id: string | null) => void;
  setSettingsOpen: (open: boolean) => void;
}

export const useAgentStore = create<AgentStreamState>((set) => ({
  activeRunId: null,
  status: "idle",
  conversationHistory: [],
  events: [],
  streamedText: "",
  tokensUsed: 0,
  iteration: 0,
  clarificationQuestion: null,
  clarificationOptions: null,
  humanTasks: null,
  humanTasksTemplate: null,
  humanTasksBrief: null,
  fundWalletRequest: null,
  selectedHistoryRunId: null,
  settingsOpen: false,

  startRun: (runId) =>
    set({
      activeRunId: runId,
      status: "planning",
      conversationHistory: [],
      events: [],
      streamedText: "",
      tokensUsed: 0,
      iteration: 0,
      clarificationQuestion: null,
      clarificationOptions: null,
      humanTasks: null,
      humanTasksTemplate: null,
      humanTasksBrief: null,
      fundWalletRequest: null,
    }),

  continueTurn: (userMessage) =>
    set((state) => {
      // Snapshot current assistant turn into history
      const history = [...state.conversationHistory];
      if (state.streamedText || state.events.length > 0) {
        history.push({
          role: "assistant",
          text: state.streamedText,
          toolEvents: state.events,
        });
      }
      // Add the new user message
      history.push({ role: "user", text: userMessage, toolEvents: [] });

      return {
        conversationHistory: history,
        status: "executing",
        events: [],
        streamedText: "",
        iteration: 0,
        clarificationQuestion: null,
        clarificationOptions: null,
        humanTasks: null,
        fundWalletRequest: null,
      };
    }),

  pushEvent: (event) =>
    set((state) => {
      const updates: Partial<AgentStreamState> = {
        events: [...state.events, event],
      };

      switch (event.type) {
        case "status_change":
          updates.status = event.status;
          break;
        case "text_delta":
          updates.streamedText = state.streamedText + event.text;
          break;
        case "text_done":
          break;
        case "step_start":
          updates.iteration = event.iteration;
          break;
        case "step_end":
          updates.tokensUsed = event.tokensUsed;
          break;
        case "clarification_needed":
          updates.clarificationQuestion = event.question;
          updates.clarificationOptions = event.options ?? null;
          break;
        case "human_task_detected":
          updates.humanTasks = event.tasks;
          updates.humanTasksTemplate = event.template ?? null;
          updates.humanTasksBrief = event.brief ?? null;
          break;
        case "fund_wallet_requested":
          updates.fundWalletRequest = {
            reason: event.reason,
            suggestedAmountUsd: event.suggestedAmountUsd,
          };
          break;
        case "run_done":
          updates.tokensUsed = event.totalTokens;
          break;
      }

      return updates;
    }),

  reset: () =>
    set({
      activeRunId: null,
      status: "idle",
      conversationHistory: [],
      events: [],
      streamedText: "",
      tokensUsed: 0,
      iteration: 0,
      clarificationQuestion: null,
      clarificationOptions: null,
      humanTasks: null,
      humanTasksTemplate: null,
      humanTasksBrief: null,
      fundWalletRequest: null,
    }),

  newChat: () =>
    set({
      activeRunId: null,
      status: "idle",
      conversationHistory: [],
      events: [],
      streamedText: "",
      tokensUsed: 0,
      iteration: 0,
      clarificationQuestion: null,
      clarificationOptions: null,
      humanTasks: null,
      humanTasksTemplate: null,
      humanTasksBrief: null,
      fundWalletRequest: null,
    }),

  loadSession: (runId, turns, status) =>
    set({
      activeRunId: runId,
      status,
      conversationHistory: turns,
      events: [],
      streamedText: "",
      tokensUsed: 0,
      iteration: 0,
      clarificationQuestion: null,
      clarificationOptions: null,
      humanTasks: null,
      humanTasksTemplate: null,
      humanTasksBrief: null,
      fundWalletRequest: null,
    }),

  setSelectedHistoryRunId: (id) => set({ selectedHistoryRunId: id }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
}));
